import React, { useEffect, useRef, useState } from 'react';
import { useCart } from '../context/CartContext';
import { fixImageUrl, optimizeCloudinaryUrl, PLACEHOLDER_IMG, unitConfigFor, priceInfo, stockInfo } from '../lib/format';

function LazyProductImage({ src, alt, className, fallback }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '140px' });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  if (!visible) {
    return <div ref={ref} className="w-full h-full bg-brand-light/60 animate-pulse" aria-label="Loading image" />;
  }

  return (
    <img
      src={src || fallback}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={className}
      onError={(e) => { e.target.onerror = null; e.target.src = fallback; }}
    />
  );
}

export default function ProductCard({ product, onOpenDetail }) {
  const { addItem, cart } = useCart();
  const [showQty, setShowQty] = useState(false);
  const [customQty, setCustomQty] = useState('');
  const [added, setAdded] = useState(false);

  const inCart = cart.find((i) => i.product_id === product.id);
  const cfg = unitConfigFor(product);
  const rawImage = (product.images && product.images[0]) || product.image_url;
  const imgSrc = optimizeCloudinaryUrl(fixImageUrl(rawImage, { width: 500 }), { width: 500 }) || PLACEHOLDER_IMG;
  const { sell, mrp, off, saveAmt } = priceInfo(product);
  const { inStock, lowStock } = stockInfo(product);
  const nameEn = product.name_en || '';
  const nameTe = product.name_te || '';

  const handleAdd = (qty) => {
    addItem(product, qty);
    setShowQty(false);
    setCustomQty('');
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleCustom = () => {
    const q = parseFloat(customQty);
    if (q >= cfg.min) handleAdd(q);
  };

  return (
    <div className="group bg-white rounded-2xl border border-black/5 shadow-soft overflow-hidden flex flex-col">
      <button
        type="button"
        onClick={() => onOpenDetail?.(product)}
        className="relative block aspect-square bg-brand-light/50 w-full"
        title="View details"
      >
        <LazyProductImage
          src={imgSrc}
          alt={nameEn}
          className="w-full h-full object-cover"
          fallback={PLACEHOLDER_IMG}
        />
        {off > 0 ? (
          <span className="absolute top-2 left-2 bg-gold text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full shadow-soft">{off}% OFF</span>
        ) : null}
        {!inStock ? (
          <span className="absolute top-2 right-2 bg-ink/80 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">Out of stock</span>
        ) : lowStock ? (
          <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">Low stock</span>
        ) : null}
        {inCart ? (
          <span className="absolute bottom-2 left-2 bg-brand-dark text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">✓ {inCart.quantity_kg}{cfg.label}</span>
        ) : null}
      </button>

      <div className="p-2.5 md:p-3 flex flex-col flex-1">
        <button type="button" onClick={() => onOpenDetail?.(product)} className="text-left">
          <div className="text-[13px] md:text-sm font-extrabold text-ink leading-snug line-clamp-2">{nameEn}</div>
          {nameTe ? <div className="text-xs text-ink-faint line-clamp-1" style={{ fontFamily: "'Tiro Telugu', serif" }}>{nameTe}</div> : null}
        </button>

        <button type="button" onClick={() => onOpenDetail?.(product)} className="flex items-baseline gap-1.5 flex-wrap mt-1 text-left">
          <span className="font-black text-ink text-[15px]">₹{sell}<span className="text-[11px] font-semibold text-ink-faint">/{cfg.label}</span></span>
          {mrp > sell ? <span className="text-[11px] text-ink-faint line-through">₹{mrp}</span> : null}
        </button>
        {saveAmt > 0 ? <div className="text-[10px] font-extrabold text-emerald-700 mt-0.5">Save ₹{saveAmt}</div> : null}

        {product.effective_return_enabled ? (
          <div className="text-[10px] font-bold text-sky-700 mt-1">↩ Returns OK</div>
        ) : null}

        <div className="mt-auto pt-2">
          {!showQty ? (
            <button
              type="button"
              onClick={() => setShowQty(true)}
              disabled={!inStock}
              className={`w-full py-2 rounded-xl text-[13px] font-extrabold text-white transition-colors ${
                !inStock ? 'bg-black/20 cursor-not-allowed' : added ? 'bg-emerald-600' : 'bg-brand-dark hover:bg-brand-deep'
              }`}
            >
              {!inStock ? 'Unavailable' : added ? '✓ Added!' : '+ Add'}
            </button>
          ) : (
            <div className="space-y-1.5">
              <div className="flex gap-1.5">
                {cfg.presets.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleAdd(p)}
                    className="flex-1 py-1.5 rounded-lg bg-brand-light text-brand-dark text-[11px] font-extrabold"
                  >
                    {p}{cfg.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <input
                  type="number"
                  placeholder={`Other ${cfg.inputHint}`}
                  value={customQty}
                  onChange={(e) => setCustomQty(e.target.value)}
                  min={cfg.min}
                  step={cfg.step}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-black/10 text-xs outline-none"
                />
                <button type="button" onClick={handleCustom} className="px-2.5 rounded-lg bg-brand-dark text-white font-extrabold text-xs">✓</button>
              </div>
              <button type="button" onClick={() => setShowQty(false)} className="w-full text-center text-[11px] text-ink-faint font-bold">Cancel</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
